import { useExecuteMSRNudge } from '@atlassian/cross-join-nudges';
import { cjnEligibilityCacheHandler, isCacheEnabled, withEligibilityCache } from './caching';
import { hasSeenAnyNudgeExceptMsr } from './has-seen-nudge-check';
import { useCallback } from 'react';
import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';
import { useOverrides } from './overrides';
import { CrossJoinNudgesInput } from './msr/types';

export const getHasBeenEnrolledInMsr = () => {
	const cachedHasEnrolledMsr = withEligibilityCache('has_enrolled_msr', async () => {
		// The first time this is accessed without being set, it will be set false
		return false;
	});
	return cachedHasEnrolledMsr();
};

export const setEnrolledInMsr = () => {
	return cjnEligibilityCacheHandler.set('has_enrolled_msr', true);
};

export const checkMsrCrossJoinNudgeEligibility = async () => {
	// MSR relies too heavily on localstorage to allow any users who have it disabled
	if (!(await isCacheEnabled())) {
		return false;
	}

	// Existing users of MSR variant/control
	if (await getHasBeenEnrolledInMsr()) {
		return true;
	}

	// Users who should be put into MSR variant/control
	if (!hasSeenAnyNudgeExceptMsr()) {
		return true;
	}

	return false;
};

export function getMsrFeatureVisited(): boolean {
	return localStorage.getItemAsBoolean(localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_KEY) || false;
}

export function setMsrFeatureVisited(daysExpiry: number): void {
	return localStorage.setItem(
		localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_KEY,
		true,
		daysExpiry * 86_400,
	);
}

export function getMsrFeatureVisitedCount(): number {
	return localStorage.getItem(localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_COUNT) || 0;
}

export function setMsrFeatureVisitedCount(count: number): void {
	return localStorage.setItem(localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_COUNT, count);
}

export const useRunMsrNudges = ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput) => {
	const overrides = useOverrides<'feature_visited_too_recently'>('cjn_msr_overrides');

	const executeNudge = useExecuteMSRNudge({
		cloudId: cloudId,
		getFeatureVisited: getMsrFeatureVisited,
		getFeatureVisitedCount: getMsrFeatureVisitedCount,
		onError: (err: unknown) => {
			// TODO: Placeholder, add error analytics
			return err;
		},
		overrides: overrides,
		product: 'confluence',
		subProduct: '',
		setFeatureVisited: setMsrFeatureVisited,
		setFeatureVisitedCount: setMsrFeatureVisitedCount,
	});

	return useCallback(async () => {
		const { getNudges } = await import(
			/* webpackChunkName: "loadable-msr-cross-join-nudges-nudges" */ './msr/msr-nudges'
		);
		const nudges = getNudges({ cloudId, userId, createAnalyticsEvent, flags, push });

		return executeNudge({ nudges });
	}, [cloudId, userId, createAnalyticsEvent, flags, push, executeNudge]);
};
